import { Control, FieldValues, Path } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./Form";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../Select";
import {
  cn,
  formInputClassName,
  inputClassName,
  labelClassName,
} from "../../..";
import { SelectProps } from "@radix-ui/react-select";
import { ReactNode } from "react";

type AcceptedValueType = string | number;

export type Item<ValueType extends AcceptedValueType = string> = {
  value: ValueType;
  label: ReactNode;
};

export type FormSelectProps<
  TForm extends FieldValues,
  ValueType extends AcceptedValueType = string,
> = {
  control: Control<TForm>;
  name: Path<TForm>;
  label?: ReactNode;
  hasMessage?: boolean;
  errorRing?: boolean;
  items: Item<ValueType>[];
  className?: string;
  placeholder?: string;
  inputClassName?: string;
} & Omit<SelectProps, "onValueChange"> & {
    onValueChange?: (value: ValueType) => void;
  };

export function FormSelect<
  TForm extends FieldValues,
  ValueType extends AcceptedValueType = string,
>({
  control,
  name,
  label,
  hasMessage = false,
  errorRing = true,
  items,
  className,
  placeholder,
  inputClassName,
  ...props
}: FormSelectProps<TForm, ValueType>) {
  const valueType = items.length > 0 ? typeof items[0].value : "string";

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value =
          valueType === "number" ? field.value.toString() : field.value;

        return (
          <FormItem className={cn(formInputClassName, className)}>
            {label ? (
              <FormLabel className={labelClassName}>{label}</FormLabel>
            ) : null}

            <Select
              {...props}
              onValueChange={(value) => {
                // NOTE : Protection against React Strict Mode which can trigger onValueChange with an empty value
                if (
                  !value &&
                  field.value &&
                  items.some((item) => item.value === field.value)
                ) {
                  return;
                }

                const newValue: ValueType = (
                  valueType === "number" ? Number(value) : value
                ) as ValueType;
                if (props.onValueChange) props.onValueChange(newValue);
                field.onChange(newValue);
              }}
              value={value}
            >
              <FormControl>
                <SelectTrigger
                  className={cn(inputClassName, {
                    "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                      fieldState.error && errorRing,
                  })}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>

              <SelectContent>
                {items.map((item, index) => (
                  <SelectItem
                    key={`${item.value}_${index}`}
                    value={item.value.toString()}
                  >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasMessage ? <FormMessage /> : null}
          </FormItem>
        );
      }}
    />
  );
}

export default FormSelect;
